import * as React from 'react'
import { tv, VariantProps } from 'tailwind-variants'
import { uploadFileIcon } from '../../../assets/svg'

const dropZoneDocumentsVariants = tv({
  base: 'flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors duration-200 hover:border-gray-400 hover:bg-gray-100',
  variants: {
    size: {
      sm: 'min-h-[100px] p-3',
      default: 'min-h-[140px] p-4',
      lg: 'min-h-[180px] p-6',
      xl: 'min-h-[220px] p-8'
    },
    isDragOver: {
      true: 'border-green-500 bg-green-50',
      false: ''
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50 hover:border-gray-300 hover:bg-gray-50',
      false: ''
    }
  },
  defaultVariants: {
    size: 'default',
    isDragOver: false,
    disabled: false
  }
})

const iconVariants = tv({
  base: 'mb-3 text-gray-400',
  variants: {
    size: {
      sm: 'h-8 w-8',
      default: 'h-10 w-10',
      lg: 'h-12 w-12',
      xl: 'h-16 w-16'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

const textVariants = tv({
  base: 'text-center text-gray-600',
  variants: {
    size: {
      sm: 'text-sm',
      default: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl'
    }
  },
  defaultVariants: {
    size: 'default'
  }
})

export interface DropZoneDocumentsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop' | 'onDragOver' | 'onDragLeave'>,
    VariantProps<typeof dropZoneDocumentsVariants> {
  onDrop?: (files: File[]) => void
  onDragOver?: (event: React.DragEvent) => void
  onDragLeave?: (event: React.DragEvent) => void
  onFileSelect?: (files: FileList | null) => void
  accept?: string
  multiple?: boolean
  disabled?: boolean
  children?: React.ReactNode
}

const DropZoneDocuments = React.forwardRef<HTMLDivElement, DropZoneDocumentsProps>(
  (
    {
      className,
      size = 'default',
      onDrop,
      onDragOver,
      onDragLeave,
      onFileSelect,
      accept,
      multiple = false,
      disabled = false,
      children,
      ...props
    },
    ref
  ) => {
    const [isDragOver, setIsDragOver] = React.useState(false)
    const fileInputRef = React.useRef<HTMLInputElement>(null)

    const handleDragOver = (event: React.DragEvent) => {
      event.preventDefault()
      if (!disabled) {
        setIsDragOver(true)
        onDragOver?.(event)
      }
    }

    const handleDragLeave = (event: React.DragEvent) => {
      event.preventDefault()
      setIsDragOver(false)
      onDragLeave?.(event)
    }

    const handleDrop = (event: React.DragEvent) => {
      event.preventDefault()
      setIsDragOver(false)

      if (!disabled) {
        const files = Array.from(event.dataTransfer.files)
        onDrop?.(files)
      }
    }

    const handleClick = () => {
      if (!disabled) {
        fileInputRef.current?.click()
      }
    }

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onFileSelect?.(event.target.files)
      }
    }

    return (
      <>
        <div
          ref={ref}
          className={dropZoneDocumentsVariants({
            size,
            isDragOver,
            disabled,
            className
          })}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
          {...props}>
          {children || (
            <>
              <img 
                src={uploadFileIcon} 
                alt="Upload file icon" 
                className={iconVariants({ size })} 
                style={{
                  filter: 'brightness(0) saturate(100%) invert(55%) sepia(8%) saturate(0%) hue-rotate(0deg) brightness(89%) contrast(87%)'
                }}
              />
              <p className={textVariants({ size })}>
                Arraste e solte seu documento aqui ou{' '}
                <span className="cursor-pointer font-medium text-primary-800 underline">clique e procure</span>
              </p>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />
      </>
    )
  }
)

DropZoneDocuments.displayName = 'DropZoneDocuments'

export { DropZoneDocuments, dropZoneDocumentsVariants }
